import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {PrimaryObjectPlan} from 'config-migration/types/MigrationObjects';
import {PRIMARY_OBJECT_TYPE_TO_DISPLAY_TEXT, PrimaryObjectType} from 'config-migration/types/ObjectType';
import getTestProps from 'test/lib/cypress/getTestProps';

import {ObjectActionType} from '../../types/objectActions';
import ObjectActionDropdown from '../ObjectActionDropdown';
import {computeFilteredActions} from '../planning-logic/ConfigMigrationPlan';
import TemplatePlanRow from '../templates-step/TemplatePlanRow';
import {ImportPlanSectionProps, ImportPlanSummary} from './ImportPlanSection';
import ImportPlanSectionRow from './ImportPlanSectionRow';

export default function GrouperPlanSection(props: ImportPlanSectionProps): JSX.Element {
  // If grouperPlan and groupedPlans are provided, use them directly
  // Otherwise, assume the first plan is the grouper and the rest are grouped plans
  const grouperPlan = props.plans[0];
  const groupedPlans = props.plans.slice(1);

  const onUpdateGrouperPlan = (updatedGrouperPlan: PrimaryObjectPlan): void => {
    if (updatedGrouperPlan.selectedAction.type === ObjectActionType.IGNORE) {
      const updatedGroupedPlans: Array<PrimaryObjectPlan> = groupedPlans.map((plan) => ({
        ...plan,
        selectedAction: {type: ObjectActionType.IGNORE},
      }));
      props.onUpdatePlans([updatedGrouperPlan, ...updatedGroupedPlans]);
    } else {
      props.onUpdatePlans([
        updatedGrouperPlan,
        ...groupedPlans.map((plan) => ({...plan, selectedAction: plan.initialAction})),
      ]);
      props.onUpdateCollapsed(false);
    }
  };

  // Determine if we should use template-specific components
  const isTemplateSection = grouperPlan.objectType === PrimaryObjectType.TemplateCollection;

  const {showIgnore, filteredAdditionalActions, showLockIcon} = computeFilteredActions(grouperPlan);

  return (
    <div className="simpleDivTable u-spacing-top">
      <div className={classNames('simpleDivTable-header', 'u-lr-split', css(styles.summaryRow))}>
        <h4>{PRIMARY_OBJECT_TYPE_TO_DISPLAY_TEXT[grouperPlan.objectType]}</h4>
        <ImportPlanSummary
          plans={props.plans}
          onUpdateCollapsed={props.onUpdateCollapsed}
          collapsed={props.collapsed}
        />
      </div>
      <div
        className="simpleDivTable-header"
        {...getTestProps({component: 'GrouperSection', element: 'tableHeader'})}
      >
        <div className={classNames('simpleDivTable-headerCell u-black-color', css(styles.firstCell))}>
          <strong>{grouperPlan.objectDisplayName}</strong>
        </div>
        <div className="simpleDivTable-headerCell">
          <ObjectActionDropdown
            object={grouperPlan}
            onUpdateObject={onUpdateGrouperPlan}
            showIgnore={showIgnore}
            filteredAdditionalActions={filteredAdditionalActions}
            disableDropdown={showLockIcon}
          />
        </div>
      </div>
      {!props.collapsed && (
        <>
          {groupedPlans.map((plan) => {
            if (isTemplateSection) {
              return (
                <TemplatePlanRow
                  key={plan.objectId}
                  plan={plan}
                  errors={props.errorsByObjectId[plan.objectId] || []}
                  onUpdatePlan={(updatedPlan) => {
                    props.onUpdatePlans([updatedPlan]);
                  }}
                  templateCollectionPlan={grouperPlan}
                />
              );
            } else {
              return (
                <ImportPlanSectionRow
                  key={plan.objectId}
                  plan={plan}
                  errors={props.errorsByObjectId[plan.objectId] || []}
                  onUpdatePlan={(updatedPlan) => {
                    props.onUpdatePlans([updatedPlan]);
                  }}
                  highImpactChangeMap={props.highImpactChangeMap}
                  nestedDepth={0}
                />
              );
            }
          })}
        </>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  summaryRow: {
    paddingLeft: 24,
    paddingRight: 24,
    marginBottom: 2,
  },
  firstCell: {
    marginLeft: 12,
  },
});
